import { StyleSheet, css } from 'aphrodite';
import { type FC, useMemo, useState } from 'react';

import {
	Combobox,
	ComboboxChip,
	ComboboxChipRemove,
	ComboboxChips,
	ComboboxChipsInput,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxItem,
	ComboboxItemCheckboxIndicator,
	ComboboxList,
} from '../../../web/components-v2/Combobox';
import { useUserAutocompleteQuery } from '../../../queries/User/useUserAutocompleteQuery';

export type CCUserPickerProps = {
	/** Selected user ids. */
	value: string[];
	/** Fired when the rep adds or removes a user. */
	onChange: (next: string[]) => void;
};

/**
 * "Add CC" picker for the wrap-up note step. Mirrors the legacy
 * `<CCField>` from `aida/components/AidaNoteEditor/index.tsx:103-114`:
 * starts as a small "Add CC" link, clicking it expands into a multi-select
 * combobox with chips that searches users by typing.
 *
 * Uses the components-v2 `<Combobox multiple>` primitive backed by
 * `useUserAutocompleteQuery` (same endpoint the legacy CCField uses). The
 * selected ids are state-only — the labels are remembered locally so chips
 * can render without re-querying. On blur with an empty selection the picker
 * collapses back to the "Add CC" link.
 */
export const CCUserPicker: FC<CCUserPickerProps> = ({ value, onChange }) => {
	const [expanded, setExpanded] = useState(value.length > 0);
	const [search, setSearch] = useState('');
	const [labelMap, setLabelMap] = useState<Record<string, string>>({});

	const usersQuery = useUserAutocompleteQuery({
		fragment: search,
		enabled: expanded,
		pageSize: 25,
	});

	// Cache the label for any currently-selected user id so chips render
	// consistently even when the autocomplete result set changes.
	const seenIds = useMemo(() => new Set(value), [value]);
	// React Query returns a stable reference for `data`, but `data?.values`
	// would otherwise create a new array each render. The whole `mergedUsers`
	// computation is wrapped in `useMemo` keyed on the query data so chips
	// don't churn between renders.
	const queryData = usersQuery.data;
	const mergedUsers = useMemo(() => {
		const merged: Array<{ id: string; label: string }> = [];
		const ids = new Set<string>();
		const fetched = queryData?.values ?? [];
		fetched.forEach(u => {
			if (!u.id) return;
			const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.primaryEmail?.value || u.id;
			merged.push({ id: u.id, label: name });
			ids.add(u.id);
		});
		// Surface any selected ids that aren't in the current page so chips
		// don't disappear when search filters them out.
		seenIds.forEach(id => {
			if (!ids.has(id)) {
				merged.push({ id, label: labelMap[id] ?? id });
			}
		});
		return merged;
	}, [queryData, seenIds, labelMap]);

	const handleValueChange = (next: string[]) => {
		// Capture labels for newly-selected ids so chips have a display string.
		const additions: Record<string, string> = {};
		next.forEach(id => {
			if (!labelMap[id]) {
				const found = mergedUsers.find(u => u.id === id);
				if (found) additions[id] = found.label;
			}
		});
		if (Object.keys(additions).length > 0) {
			setLabelMap(prev => ({ ...prev, ...additions }));
		}
		onChange(next);
	};

	if (!expanded) {
		return (
			<button type='button' className={css(styles.addCcLink)} onClick={() => setExpanded(true)}>
				Add CC
			</button>
		);
	}

	return (
		<div className={css(styles.root)}>
			<span className={css(styles.label)}>CC:</span>
			{/* The Base UI Combobox's `onValueChange` is parameterized over the
			    item shape; this picker stores ids as plain strings, so cast
			    through `unknown` so the prop accepts our typed callback. */}
			<Combobox multiple value={value} onValueChange={handleValueChange as unknown as () => void}>
				<ComboboxChips styles={[styles.chipsRow]}>
					{value.map(id => (
						<ComboboxChip key={id}>
							{labelMap[id] ?? id}
							<ComboboxChipRemove />
						</ComboboxChip>
					))}
					<ComboboxChipsInput
						placeholder={value.length === 0 ? 'Search users…' : ''}
						value={search}
						onChange={e => setSearch(e.target.value)}
					/>
				</ComboboxChips>
				<ComboboxContent>
					<ComboboxList>
						{mergedUsers.length === 0 ? (
							<ComboboxEmpty>{usersQuery.isLoading ? 'Loading…' : 'No users found'}</ComboboxEmpty>
						) : (
							mergedUsers.map(u => (
								<ComboboxItem key={u.id} value={u.id}>
									<ComboboxItemCheckboxIndicator />
									{u.label}
								</ComboboxItem>
							))
						)}
					</ComboboxList>
				</ComboboxContent>
			</Combobox>
		</div>
	);
};

const styles = StyleSheet.create({
	root: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	label: {
		fontSize: 12,
		fontWeight: 500,
		color: 'var(--ink-muted)',
	},
	chipsRow: {
		flex: 1,
	},
	addCcLink: {
		alignSelf: 'flex-start',
		background: 'transparent',
		border: 'none',
		color: 'var(--accent)',
		cursor: 'pointer',
		fontSize: 12,
		fontWeight: 500,
		padding: 0,
	},
});
